// Where this computer's WeighBridge identity lives.
//
// ─── Why the licence pair is stored, not typed every shift ─────────────
// The WeighBridge ID and License Key identify the SHOP. They never change between
// shifts, and they live on a licence certificate rather than in a counter operator's
// head.
//
// Asking for them at every sign-in has one predictable outcome: the certificate ends
// up taped to the monitor, where everyone who walks past can read it. Storing them
// once, on the machine that is already trusted to display the shop's weighments, is
// the better trade — and it makes the daily sign-in two fields instead of four.
//
// So: activate once per computer, then username and password forever after.
//
// ─── What is NOT stored ────────────────────────────────────────────────
// The password. Never. Only the pair that names the shop, and the receiver URL.
//
// The key is held as typed rather than hashed, because it has to be REPLAYED to the
// server on each login - it is a credential this machine presents, not one it
// verifies. That is a real exposure and it is the reason activation is a deliberate,
// once-per-machine act rather than something that happens invisibly.
import type { Session } from '../api/client';

const KEY = 'scale-bridge.activation';

export interface Activation {
    baseUrl: string;
    weighbridgeId: string;
    licenseKey: string;
    /** Shown on the sign-in screen so an operator can see which shop this is. */
    accountName?: string;
}

export function loadActivation(): Activation | null {
    try {
        const raw = localStorage.getItem(KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Activation;
        // A half-written entry is worse than none: it would send an incomplete pair
        // and produce a "wrong credentials" message about something the operator
        // never typed.
        if (!parsed?.weighbridgeId || !parsed?.licenseKey) return null;
        return {
            baseUrl: parsed.baseUrl ?? '',
            weighbridgeId: parsed.weighbridgeId,
            licenseKey: parsed.licenseKey,
            accountName: parsed.accountName,
        };
    } catch {
        // A corrupt entry should send the operator to activation, not white-screen
        // the app.
        return null;
    }
}

export function saveActivation(a: Activation): void {
    localStorage.setItem(KEY, JSON.stringify(a));
}

export function clearActivation(): void {
    localStorage.removeItem(KEY);
}

/**
 * A stored pair that has stopped working is the EXPECTED failure, not an exotic one:
 * a renewal issues a new key and the old one stops resolving.
 *
 * That case has to reopen activation with an explanation. Showing "invalid
 * credentials" instead sends the operator to reset a password that was never wrong —
 * which is the kind of support call that takes an afternoon.
 */
export function isStaleLicence(err: unknown): boolean {
    return err instanceof Error && err.name === 'LoginError';
}

/** Convenience: the session shape the API client wants, once a token exists. */
export function sessionFrom(
    activation: Activation,
    token: string,
    username: string,
    accountName: string,
): Session {
    return { baseUrl: activation.baseUrl, token, username, accountName };
}
